import { useMemo } from 'react';
import { TFunction } from 'i18next';

import { Event } from 'events/_types';
import {
  FilterOptions,
  SignalOption,
} from 'events/components/FilterGroupingControl/_types';
import {
  VISIBLE_ENTITY_TAG_CLASSES,
  formatCategoryLabel,
} from 'shiftNotes/helpers/entityTagUtils';

interface Department {
  id: string;
  name: string;
}

export default function useEventFilterOptions(
  events: Event[],
  departmentsData: Department[] | undefined,
  signals: SignalOption[],
  t: TFunction
): FilterOptions {
  const peopleOptions = useMemo(() => {
    const seen = new Map<
      string,
      { id: string; name: string; initials: string }
    >();
    events.forEach((event) => {
      event.shift_notes?.forEach((note) => {
        if (!note.user || seen.has(note.user.id)) return;
        const name = `${note.user.first_name} ${note.user.last_name}`.trim();
        if (!name) return;
        const initials = `${note.user.first_name.charAt(
          0
        )}${note.user.last_name.charAt(0)}`.toUpperCase();
        seen.set(note.user.id, { id: note.user.id, name, initials });
      });
    });
    return Array.from(seen.values())
      .map((person) => ({ ...person, role: '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  return useMemo<FilterOptions>(
    () => ({
      departments: (departmentsData ?? []).map((dept) => ({
        id: dept.id,
        name: dept.name,
      })),
      people: peopleOptions,
      tags: VISIBLE_ENTITY_TAG_CLASSES.map((label) => {
        const key = label.toLowerCase().replace(/\s+/g, '_');
        return {
          id: label,
          name: t(`pageShiftNotes:extractionClass.${key}`, {
            defaultValue: formatCategoryLabel(key),
          }),
        };
      }).sort((a, b) => a.name.localeCompare(b.name)),
      signals: [...signals].sort((a, b) => a.name.localeCompare(b.name)),
    }),
    [departmentsData, peopleOptions, signals, t]
  );
}
